import { useEffect, useState, type FormEvent } from 'react'
import { Brand } from '../components/Brand'
import { useAuth } from '../lib/auth'
import { api, errorMessage } from '../lib/api'
import { Banner } from '../components/ui'

/**
 * ONE SIGN-IN PAGE PER PRACTICE ADDRESS (wow.lynqpod.com/login, tsd.lynqpod.com/login):
 * the team signs in here. On the Lynqpod console's own address (app.lynqpod.com) it is the
 * console's sign-in, and a practice's team is pointed at their own address instead.
 *
 * TWO PANELS ON A WIDE SCREEN, ONE ON A NARROW ONE. The form used to be a small card adrift
 * in the middle of an empty page, which reads as unfinished on the large screen a practice
 * manager actually uses. The left panel carries the practice's own name and address in the
 * brand's gradient, so the first thing somebody sees is that they are in the right place;
 * the right holds nothing but the form. Below lg the panel is dropped rather than stacked,
 * because on a phone it would push the fields under the fold.
 *
 * ⚠️ THIS PAGE DOES NOT FOLLOW THE PRACTICE'S THEME, and that is deliberate. Every other
 * screen uses `brand`, a CSS variable, so a practice can reskin the application it works in
 * all day. Sign-in is the one screen that is Lynqpod's rather than the practice's: it carries
 * our wordmark and our address. So it is painted in `lynq`, the marketing site's own palette
 * copied from the stylesheet lynqpod.com serves, and the Ledger skin leaves it alone.
 */
interface Where { console: boolean; practiceUrl: string; practiceName: string }

export default function Login() {
  // ?expired=1 is set by the api layer when a request comes back 401 (lib/api.ts).
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
  const expired = params.get('expired') === '1'
  const next = params.get('next') || ''

  const { signIn } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [moveTo, setMoveTo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [where, setWhere] = useState<Where | null>(null)
  useEffect(() => { api.get<Where>('/where').then((r) => setWhere(r.data)).catch(() => setWhere(null)) }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr(null); setMoveTo(null); setBusy(true)
    try {
      await signIn(username.trim(), password)
      /*
       * Back where they were. `next` only ever comes from our own redirect, but it arrives
       * through the address bar, so only a path on this site is followed: "//evil.example"
       * and "https://…" are ignored rather than becoming an open redirect.
       */
      if (next.startsWith('/') && !next.startsWith('//')) window.location.assign(next)
    }
    catch (x) {
      // The old client reported every failure as "Invalid credentials", which sent
      // us hunting a password problem when the real cause was a CORS rejection.
      setErr(errorMessage(x, 'Could not sign in.'))
      const to = (x as { response?: { data?: { moveTo?: string } } })?.response?.data?.moveTo
      if (to) setMoveTo(to)
    }
    finally { setBusy(false) }
  }

  const onConsole = !!where?.console
  const name = where?.practiceName || ''
  const host = typeof window !== 'undefined' ? window.location.host : ''
  const INPUT = 'w-full rounded-lg border border-lynq-line bg-white px-3.5 py-2.5 text-sm text-lynq-text shadow-sm outline-none transition-colors placeholder:text-lynq-dim focus:border-lynq-brand focus:ring-2 focus:ring-lynq-brand/20'

  return (
    <div className="flex min-h-screen bg-lynq-paper-2">
      {/* ── the practice's own side ──────────────────────────────────────── */}
      <div className="relative hidden w-[45%] flex-col justify-between overflow-hidden bg-gradient-to-br from-lynq-ink via-lynq-ink-3 to-lynq-brand-d p-12 lg:flex xl:w-1/2">
        {/* Two soft shapes, off the edges, so the panel is not a flat rectangle of colour. */}
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/5" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-white/5" />
        <Brand size={30} tone="white" className="relative" />
        <div className="relative">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight text-white">
            {onConsole ? 'The Lynqpod console' : name || 'Your practice'}
          </h2>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/70">
            {onConsole
              ? 'Every practice on Lynqpod, and whether each one is working.'
              : 'The diary, the money and the people who do the work, in one place.'}
          </p>
        </div>
        <p className="relative font-mono text-xs text-white/40">{host}</p>
      </div>

      {/* ── the form ─────────────────────────────────────────────────────── */}
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-[55%] lg:px-16 xl:w-1/2">
        <div className="mx-auto w-full max-w-[22rem]">
          <div className="mb-10 lg:hidden"><Brand size={30} /></div>

          <h1 className="text-2xl font-semibold tracking-tight text-lynq-text">Sign in</h1>
          <p className="mt-1.5 text-sm text-lynq-muted">
            {onConsole ? 'For the people who run Lynqpod.'
              : name ? `${/^the\b/i.test(name) ? '' : 'The '}${name} team.` : 'Your username or work email, and your password.'}
          </p>

          <div className="mt-8">
            {/*
              * Arrived here because a session ran out, rather than by choosing to sign in.
              * Saying so is the difference between "something went wrong" and "that is just
              * how long a session lasts", and it is the one thing the old "Unauthorized"
              * banner on a half-finished payroll run never explained.
              */}
            {expired && !err && (
              <Banner kind="info">
                Your session had been open a while, so you were signed out. Sign in and you will
                go back to where you were.
              </Banner>
            )}
            {err && <Banner kind="error">{err}</Banner>}
            {moveTo && (
              <p className="-mt-2 mb-4 text-sm">
                <a className="font-medium text-lynq-brand underline" href={moveTo}>
                  Go to {moveTo.replace(/^https:\/\//, '').replace(/\/login$/, '')}
                </a>
              </p>)}

            <form onSubmit={submit} className="space-y-5">
              <div>
                <label htmlFor="u" className="mb-1.5 block text-sm font-medium text-lynq-text">Username or email</label>
                <input id="u" value={username} onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username" autoFocus className={INPUT} />
              </div>
              <div>
                <label htmlFor="p" className="mb-1.5 block text-sm font-medium text-lynq-text">Password</label>
                <input id="p" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password" className={INPUT} />
              </div>
              {/*
                * ⚠️ NOT THE SHARED <Button>. That one is a diagonal gradient from brand-900 to
                * brand-600, which under a dark-to-mid palette reads as washed-out sage at
                * button size and lost the brand entirely. lynqpod.com's own .btn-primary is a
                * flat fill of one colour, so this is a flat fill of that same colour.
                */}
              <button type="submit" disabled={busy || !username || !password}
                className="press w-full rounded-[10px] bg-lynq-brand py-2.5 text-sm font-semibold text-white transition-colors hover:bg-lynq-brand-d disabled:cursor-not-allowed disabled:bg-lynq-brand/40">
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>

          {onConsole && where?.practiceUrl && (
            <p className="mt-8 text-sm text-lynq-muted">
              Practice team? Sign in at your own address,{' '}
              <a className="font-medium text-lynq-brand hover:underline" href={`${where.practiceUrl}/login`}>
                {where.practiceUrl.replace(/^https:\/\//, '')}/login
              </a>
            </p>)}

          <p className="mt-10 text-xs text-lynq-dim lg:hidden">{host}</p>
        </div>
      </div>
    </div>
  )
}
